import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SensitiveData } from '@/components/sensitive-data';
import { KPI } from '@/components/ui/kpi';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import {
  TrendingUp,
  TrendingDown,
  Search,
  X,
  AlertTriangle,
  ExternalLink,
  Info,
  Clock,
  DollarSign,
  Wallet,
  TriangleAlert,
  Download,
  Loader2,
} from 'lucide-react';
import { fetchProjectForecastDataPaged, fetchProjects } from '@/lib/data-service';
import type { ForecastReportParams } from '@/lib/data-service';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import { calculateEAC, formatCurrency, formatDate, healthToVariant } from '@/lib/utils';
import type { EacMethod } from '@/lib/utils';
import type { ProjectForecastData, ForecastSummary, ForecastFilterOptions } from '@/types/reports.types';
import type { PaginationMetadata } from '@/types/pagination';
import { ROUTES } from '@/routes';
import { PaginationControls } from '@/components/pagination-controls';
import { DEFAULT_PAGE_SIZE } from '@/types/pagination';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { exportForecastToExcel } from '@/lib/export-forecast-excel';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getDelayDays(project: ProjectForecastData): number | null {
  if (!project.forecastEndDate || !project.endDate) return null;
  const forecast = new Date(project.forecastEndDate).getTime();
  const planned = new Date(project.endDate).getTime();
  const diff = Math.round((forecast - planned) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}

/** Variance colour: positive = overrun (bad) = red, negative = under budget (good) = green */
function VariancePill({ value, isPercent = false }: { value: number; isPercent?: boolean }) {
  const absVal = Math.abs(value);
  const threshold = isPercent ? 2 : 0;

  if (absVal <= threshold) {
    return <span className="text-muted-foreground text-sm">≈ On plan</span>;
  }

  const label = isPercent
    ? `${value > 0 ? '+' : '−'}${absVal.toFixed(1)}%`
    : `${value > 0 ? '+' : '−'}${formatCurrency(absVal)}`;

  const colour = value > 0
    ? 'text-error-600 dark:text-error-400'
    : 'text-success-600 dark:text-success-400';

  return (
    <span className={`text-sm font-medium ${colour}`}>
      {value > 0 ? <TrendingUp className="inline h-3 w-3 mr-0.5" /> : <TrendingDown className="inline h-3 w-3 mr-0.5" />}
      {label}
    </span>
  );
}

function HealthBadge({ health, label }: { health: 'green' | 'amber' | 'red'; label?: string }) {
  const variant = healthToVariant(health);
  const defaultLabel = health === 'green' ? 'On Track' : health === 'amber' ? 'At Risk' : 'Off Track';
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label ?? defaultLabel}
    </Badge>
  );
}

function CpiLabel({ cpi }: { cpi: number }) {
  if (cpi >= 1.0) {
    return <span className="text-success-600 text-sm">CPI: {cpi.toFixed(2)} 🟢 Performing ahead of plan</span>;
  }
  if (cpi >= 0.9) {
    return <span className="text-warning-600 text-sm">CPI: {cpi.toFixed(2)} 🟡 Slightly behind plan</span>;
  }
  return <span className="text-error-600 text-sm">CPI: {cpi.toFixed(2)} 🔴 Significantly behind plan</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Enriched row type
// ─────────────────────────────────────────────────────────────────────────────

interface ForecastRow {
  project: ProjectForecastData;
  totalBudget: number;
  eac: number;
  forecastVariance: number;
  forecastVariancePct: number;
  eacMarginPct: number | null;
  health: 'green' | 'amber' | 'red';
  pmName: string;
  delayDays: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Side Sheet
// ─────────────────────────────────────────────────────────────────────────────

interface SideSheetProps {
  row: ForecastRow;
  globalMethod: EacMethod;
  onClose: () => void;
}

function ForecastSideSheet({ row, globalMethod, onClose }: SideSheetProps) {
  const navigate = useNavigate();
  const { project, totalBudget } = row;

  // Per-project EAC method override — defaults to global
  const [sheetMethod, setSheetMethod] = useState<EacMethod>(globalMethod);
  // Re-estimate input state (session-only)
  const [etc, setEtc] = useState<number>(0);
  const [etcInput, setEtcInput] = useState<string>('0');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync sheet method when global changes (unless user has overridden it)
  const [hasOverridden, setHasOverridden] = useState(false);
  useEffect(() => {
    if (!hasOverridden) setSheetMethod(globalMethod);
  }, [globalMethod, hasOverridden]);

  const differsFromGlobal = sheetMethod !== globalMethod;

  const completionPercent = project.completionPercent ?? 0;
  const actualCosts = project.forecastInputs.actualCosts;

  const eacResult = useMemo(
    () =>
      totalBudget > 0
        ? calculateEAC(
            { actualCosts, totalBudget, completionPercent, estimateToComplete: etc },
            sheetMethod
          )
        : null,
    [actualCosts, totalBudget, completionPercent, sheetMethod, etc]
  );

  const projectedFinalRevenue = project.revenueData.projectedFinalRevenue;

  const budgetRevenue = project.revenueData.budgetRevenue;

  // Margin calculations
  const budgetMarginPct = budgetRevenue > 0 ? ((budgetRevenue - totalBudget) / budgetRevenue) * 100 : null;
  const forecastMarginPct =
    projectedFinalRevenue && projectedFinalRevenue > 0 && eacResult
      ? ((projectedFinalRevenue - eacResult.eac) / projectedFinalRevenue) * 100
      : null;

  // Hours
  const budgetHours = project.hoursData.budgetHours;
  const actualHours = project.hoursData.actualHours;
  // Linear projection for EAC hours
  const eacHours =
    completionPercent > 0
      ? Math.round((actualHours / completionPercent) * 100)
      : budgetHours;

  const handleEtcChange = (val: string) => {
    setEtcInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const parsed = parseFloat(val.replace(/,/g, ''));
      setEtc(isNaN(parsed) ? 0 : parsed);
    }, 300);
  };

  return (
    <Sheet open onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span>{project.projectName}</span>
            {project.projectCode && (
              <Badge variant="outline" className="text-xs font-mono">
                {project.projectCode}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription asChild>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div><span className="font-medium text-foreground">Client:</span> {project.clientName}</div>
              <div><span className="font-medium text-foreground">PM:</span> {row.pmName}</div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Status:</span>
                <Badge variant="outline" className="text-xs">{project.status}</Badge>
                <HealthBadge health={row.health} />
              </div>
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-4 space-y-6">

          {/* EAC Method per-project override */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">EAC Method</span>
              {differsFromGlobal && (
                <span className="text-xs text-muted-foreground italic">
                  (differs from table — showing {sheetMethod === 'performance-based' ? 'Performance-Based' : sheetMethod === 're-estimate' ? 'Re-estimate' : 'Simple'})
                </span>
              )}
            </div>
            <div className="flex gap-1">
              {(['simple', 'performance-based', 're-estimate'] as EacMethod[]).map(m => (
                <button
                  key={m}
                  onClick={() => { setSheetMethod(m); setHasOverridden(true); }}
                  className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                    sheetMethod === m
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:bg-accent'
                  }`}
                >
                  {m === 'simple' ? 'Simple' : m === 'performance-based' ? 'Performance-Based' : 'Re-estimate'}
                </button>
              ))}
            </div>

            {sheetMethod === 're-estimate' && (
              <div className="space-y-1 pt-1">
                <label className="text-xs text-muted-foreground">Your estimate to complete from today (£)</label>
                <Input
                  type="number"
                  min={0}
                  placeholder="0"
                  value={etcInput}
                  onChange={e => handleEtcChange(e.target.value)}
                  className="max-w-[200px] h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  EAC = Actual Costs ({formatCurrency(actualCosts)}) + Your Estimate ({formatCurrency(etc)})
                </p>
                <p className="text-xs text-muted-foreground italic flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  This estimate is session-only — it will not be saved.
                </p>
              </div>
            )}
          </div>

          {/* Cost Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Cost Forecast</h3>
            {totalBudget === 0 ? (
              <p className="text-sm text-muted-foreground">No cost budget has been configured for this project.</p>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Budget Cost</span>
                  <SensitiveData><span className="font-medium">{formatCurrency(totalBudget)}</span></SensitiveData>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Actual Cost</span>
                  <SensitiveData><span className="font-medium">{formatCurrency(actualCosts)}</span></SensitiveData>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completion</span>
                  <span className="font-medium">{completionPercent}%</span>
                </div>
                {completionPercent === 0 && (
                  <p className="text-xs text-muted-foreground italic flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    EAC equals full budget when completion is 0%.
                  </p>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground font-medium">EAC Cost</span>
                  {eacResult ? (
                    <SensitiveData>
                      <span className="font-semibold">{formatCurrency(eacResult.eac)}</span>
                    </SensitiveData>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                {eacResult && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Variance vs Budget</span>
                    <SensitiveData>
                      <VariancePill value={eacResult.costVarianceAtCompletion} />
                    </SensitiveData>
                  </div>
                )}
                {sheetMethod === 'performance-based' && eacResult?.cpi != null && (
                  <div className="pt-1">
                    <CpiLabel cpi={eacResult.cpi} />
                  </div>
                )}
                {sheetMethod === 're-estimate' && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>ETC entered</span>
                    <span>{formatCurrency(etc)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Revenue Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Revenue Forecast</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Budget Revenue</span>
                <SensitiveData><span className="font-medium">{formatCurrency(budgetRevenue)}</span></SensitiveData>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Revenue</span>
                <SensitiveData><span className="font-medium">{formatCurrency(project.revenueData.actualSales)}</span></SensitiveData>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground font-medium">Proj. Final Revenue</span>
                {projectedFinalRevenue != null ? (
                  <SensitiveData><span className="font-semibold">{formatCurrency(projectedFinalRevenue)}</span></SensitiveData>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>
          </div>

          {/* Margin Forecast */}
          {(budgetMarginPct != null || forecastMarginPct != null) && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Margin Forecast</h3>
              <div className="space-y-2 text-sm">
                {budgetMarginPct != null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Budget Margin</span>
                    <SensitiveData><span className="font-medium">{budgetMarginPct.toFixed(1)}%</span></SensitiveData>
                  </div>
                )}
                {forecastMarginPct != null && (
                  <>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-muted-foreground font-medium">Forecast Margin</span>
                      <SensitiveData><span className="font-semibold">{forecastMarginPct.toFixed(1)}%</span></SensitiveData>
                    </div>
                    {budgetMarginPct != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">vs Budget</span>
                        <SensitiveData>
                          <VariancePill value={-(forecastMarginPct - budgetMarginPct)} isPercent />
                        </SensitiveData>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Hours Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Hours Forecast</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Budget Hours</span>
                <span className="font-medium">{budgetHours.toLocaleString()} hrs</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Hours</span>
                <span className="font-medium">{actualHours.toLocaleString()} hrs</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground font-medium">EAC Hours</span>
                <span className="font-semibold">{eacHours.toLocaleString()} hrs</span>
              </div>
              {budgetHours > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Variance</span>
                  <VariancePill value={eacHours - budgetHours} />
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Schedule</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Planned End</span>
                <span className="font-medium">{project.endDate ? formatDate(project.endDate) : '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Forecast End</span>
                {project.forecastEndDate ? (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatDate(project.forecastEndDate)}</span>
                    {row.delayDays && (
                      <span className="text-warning-600 text-xs flex items-center gap-0.5">
                        <Clock className="h-3 w-3" />
                        {row.delayDays} days late
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-muted-foreground">Not set</span>
                    <button
                      onClick={() => navigate(`/projects/${project.projectId}/overview`)}
                      className="text-xs text-primary hover:underline flex items-center gap-0.5"
                    >
                      Update on project record <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer links */}
          <div className="border-t pt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`${ROUTES.BUDGET_VARIANCE_REPORT}?reportProjectId=${project.projectId}`)}
            >
              View Budget Variance →
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/projects/${project.projectId}/overview`)}
            >
              Open Project →
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function ProjectForecastReportPage() {
  const [searchParams] = useSearchParams();
  // Read ?managerId= from URL (passed by homepage PM quick link)
  const initManagerId = searchParams.get('managerId') ? Number(searchParams.get('managerId')) : null;
  const initReportProjectId = searchParams.get('reportProjectId');

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectForecastData[]>([]);
  const [serverSummary, setServerSummary] = useState<ForecastSummary | null>(null);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [filterOptions, setFilterOptions] = useState<ForecastFilterOptions>({ projectManagers: [], statuses: [] });
  const { handleApiError } = useErrorHandler({ useToast: true });

  // Filter state — initialise search from deep-link project code
  const [searchTerm, setSearchTerm] = useState('');
  const [pmFilter, setPmFilter] = useState<string>('all');
  const [billableFilter, setBillableFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(initReportProjectId ? 'all' : 'active');

  // Deep-link: fetch the project to get its code for the search filter
  useEffect(() => {
    if (initReportProjectId) {
      import('@/lib/data-service').then(({ fetchProjectById }) => {
        fetchProjectById(Number(initReportProjectId)).then((p) => {
          if (p) {
            const code = (p as any).code || (p as any).name || '';
            setSearchTerm(code);
          }
        }).catch(() => { /* ignore */ });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EAC method — global selector
  const [globalMethod, setGlobalMethod] = useState<EacMethod>('simple');

  // Sort state — default: project name ascending (server-side sort keys)
  const [sortKey, setSortKey] = useState<string>('project');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Column sort state for the DataTableEnhanced
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isExporting, setIsExporting] = useState(false);

  // Side sheet
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  // Build fetch params — status "active" maps to statusIds 1,2 on the backend
  const fetchParams = useMemo((): ForecastReportParams => ({
    page: currentPage,
    pageSize,
    status: statusFilter === 'active' ? 'active' : statusFilter !== 'all' ? statusFilter : undefined,
    pm: pmFilter !== 'all' ? pmFilter : undefined,
    billable: billableFilter !== 'all' ? billableFilter : undefined,
    search: debouncedSearch || undefined,
    sortBy: sortKey,
    sortOrder: sortDir,
  }), [currentPage, pageSize, statusFilter, pmFilter, billableFilter, debouncedSearch, sortKey, sortDir]);

  // ── Load projects ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setFetchError(null);
      try {
        const result = await fetchProjectForecastDataPaged(fetchParams);
        if (cancelled) return;
        setProjects(result.projects);
        setServerSummary(result.summary);
        setPagination(result.pagination);
        setFilterOptions(result.filterOptions);
      } catch (error) {
        if (!cancelled) {
          handleApiError(error, '/api/reports/project-forecast', 'GET');
          setFetchError('Unable to load project forecast data. The report endpoint may not be available.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [fetchParams, handleApiError]);

  // Seed pmFilter from ?managerId= once project data is loaded.
  // Forecast rows use pmName (string) for filtering. We fetch projects filtered
  // by pmId to resolve the manager's name, then seed the filter dropdown.
  useEffect(() => {
    if (!initManagerId || loading || projects.length === 0) return;
    fetchProjects('projects', undefined, { pmId: initManagerId })
      .then(matchedProjects => {
        if (matchedProjects.length === 0) return;
        const matchedIds = new Set(matchedProjects.map((p: { id: number }) => p.id));
        const match = projects.find(p => matchedIds.has(p.projectId));
        if (match?.projectManager) setPmFilter(match.projectManager);
      })
      .catch(() => { /* silent — filter just won't be pre-seeded */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, projects]);

  // ── Enrich rows (EAC is still client-side) ─────────────────────────────────
  const enriched: ForecastRow[] = useMemo(() => {
    return projects.map(project => {
      const totalBudget = project.forecastInputs.totalBudget;
      const actualCosts = project.forecastInputs.actualCosts;
      const completionPercent = project.forecastInputs.completionPercent;

      const eacResult =
        totalBudget > 0 && completionPercent >= 0
          ? calculateEAC({ actualCosts, totalBudget, completionPercent }, globalMethod)
          : null;

      const eac = eacResult?.eac ?? totalBudget;
      const forecastVariance = eac - totalBudget;
      const forecastVariancePct = totalBudget > 0 ? (forecastVariance / totalBudget) * 100 : 0;

      const projectedFinalRevenue = project.revenueData.projectedFinalRevenue;
      const eacMarginPct =
        projectedFinalRevenue && projectedFinalRevenue > 0
          ? ((projectedFinalRevenue - eac) / projectedFinalRevenue) * 100
          : null;

      return {
        project,
        totalBudget,
        eac,
        forecastVariance,
        forecastVariancePct,
        eacMarginPct,
        health: project.health,
        pmName: project.projectManager || '—',
        delayDays: getDelayDays(project),
      };
    });
  }, [projects, globalMethod]);

  // ── PM options for filter ───────────────────────────────────────────────────
  const pmOptions = useMemo(() => {
    return filterOptions.projectManagers;
  }, [filterOptions]);

  // ── Reset page when filters change ─────────────────────────────────────────
  const prevFilterKey = useRef('');
  const filterKey = `${statusFilter}|${pmFilter}|${billableFilter}|${debouncedSearch}`;
  if (filterKey !== prevFilterKey.current) {
    prevFilterKey.current = filterKey;
    if (currentPage !== 1) setCurrentPage(1);
  }

  // ── Summary cards (EAC totals still computed client-side from server page) ─
  const summary = useMemo(() => {
    const totalEac = enriched.reduce((sum, r) => sum + r.eac, 0);
    const totalBudget = serverSummary?.totalBudgetCost ?? enriched.reduce((sum, r) => sum + r.totalBudget, 0);
    const forecastVariance = totalEac - totalBudget;
    const atRisk = serverSummary?.atRiskCount ?? enriched.filter(r => r.health === 'amber' || r.health === 'red').length;
    const totalProjects = serverSummary?.totalProjects ?? enriched.length;
    return { totalEac, totalBudget, forecastVariance, atRisk, totalProjects };
  }, [enriched, serverSummary]);

  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // ── Handle column sort changes ─────────────────────────────────────────────
  const handleColumnSortChange = useCallback((sorts: ColumnSort[]) => {
    setColumnSorts(sorts);
    if (sorts.length > 0) {
      setSortKey(sorts[0].columnId);
      setSortDir(sorts[0].direction);
    }
    setCurrentPage(1);
  }, []);

  // ── Selected row for side sheet ────────────────────────────────────────────
  const selectedRow = useMemo(
    () => enriched.find(r => r.project.projectId === selectedProjectId) ?? null,
    [enriched, selectedProjectId]
  );

  // ── Row click handler ──────────────────────────────────────────────────────
  const handleRowClick = useCallback(
    (row: ForecastRow) => setSelectedProjectId(row.project.projectId),
    []
  );

  // ── Export to Excel ──────────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      const allResult = await fetchProjectForecastDataPaged({
        ...fetchParams,
        page: 1,
        pageSize: 50000,
      });
      await exportForecastToExcel(allResult.projects, globalMethod);
      toast.success(`Export complete — ${allResult.projects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, fetchParams, globalMethod]);

  // ── Column definitions — memoised so TanStack Table doesn't reinitialise ──
  const columns: ColumnDef<ForecastRow>[] = useMemo(() => [
    {
      id: 'health',
      header: 'Health',
      cell: ({ row }) => <HealthBadge health={row.original.health} />,
    },
    {
      id: 'project',
      header: 'Project',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{row.original.project.projectName}</div>
          {row.original.project.projectCode && (
            <div className="text-xs text-muted-foreground font-mono">{row.original.project.projectCode}</div>
          )}
        </div>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      cell: ({ row }) => <span className="text-sm">{row.original.project.clientName}</span>,
    },
    {
      id: 'pm',
      header: 'PM',
      cell: ({ row }) => <span className="text-sm">{row.original.pmName}</span>,
    },
    {
      id: 'billable',
      header: 'Billable',
      cell: ({ row }) => {
        const isBillable = row.original.project.billable ?? true;
        return isBillable
          ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
          : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>;
      },
    },
    {
      id: 'budget',
      header: 'Budget Cost',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <span className="text-sm font-medium">{formatCurrency(row.original.totalBudget)}</span>
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">No budget</span>
        ),
    },
    {
      id: 'actual',
      header: 'Actual Cost',
      cell: ({ row }) => (
        <SensitiveData>
          <span className="text-sm">{formatCurrency(row.original.project.forecastInputs.actualCosts)}</span>
        </SensitiveData>
      ),
    },
    {
      id: 'eac',
      header: 'EAC Cost',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <span className="text-sm font-medium">{formatCurrency(row.original.eac)}</span>
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">No budget</span>
        ),
    },
    {
      id: 'variance',
      header: 'Forecast Variance £',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <VariancePill value={row.original.forecastVariance} />
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'variancePct',
      header: 'Forecast Variance %',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <VariancePill value={row.original.forecastVariancePct} isPercent />
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'margin',
      header: 'EAC Margin',
      cell: ({ row }) => {
        const { eacMarginPct } = row.original;
        if (eacMarginPct == null) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        const marginClass =
          eacMarginPct < thresholds.marginRedThreshold
            ? 'text-error-600'
            : eacMarginPct < thresholds.marginAmberThreshold
            ? 'text-warning-600'
            : 'text-success-600';
        return (
          <SensitiveData>
            <span className={`text-sm font-medium ${marginClass}`}>{eacMarginPct.toFixed(1)}%</span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecastEnd',
      header: 'Forecast End',
      cell: ({ row }) => {
        const date = row.original.project.forecastEndDate ?? row.original.project.endDate ?? null;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm">{date ? formatDate(date) : '—'}</span>
            {row.original.delayDays && (
              <span className="text-xs text-warning-600 flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {row.original.delayDays}d late
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'completion',
      header: 'Completion %',
      cell: ({ row }) => {
        const pct = row.original.project.completionPercent;
        return (
          <div className="w-20 space-y-1">
            <div className="text-xs font-medium">{pct}%</div>
            <Progress value={pct} className="h-1.5" />
          </div>
        );
      },
    },
  ], []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-10">
      <PageHeaderBlock
        title="Project Forecast"
        subtitle="Forward-looking cost and revenue projections across all active projects."
      />

      {/* EAC Method Selector + Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-tour="project-forecast-filters">
        {/* EAC Method */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">EAC Method:</span>
          <div className="flex gap-1">
            {(['simple', 'performance-based', 're-estimate'] as EacMethod[]).map(m => (
              <button
                key={m}
                onClick={() => setGlobalMethod(m)}
                className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                  globalMethod === m
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-accent'
                }`}
              >
                {m === 'simple' ? 'Simple' : m === 'performance-based' ? 'Performance-Based' : 'Re-estimate'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1" />

        {/* PM Filter */}
        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="All PMs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PMs</SelectItem>
            {pmOptions.map(pm => (
              <SelectItem key={pm} value={pm}>{pm}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Billable Filter */}
        <Select value={billableFilter} onValueChange={setBillableFilter}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            <SelectItem value="billable">Billable</SelectItem>
            <SelectItem value="non-billable">Non-billable</SelectItem>
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Projects</SelectItem>
            <SelectItem value="all">All Projects</SelectItem>
            <SelectItem value="2">In Progress</SelectItem>
            <SelectItem value="1">Planning</SelectItem>
            <SelectItem value="3">Completed</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm w-52"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Export to Excel */}
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9"
          onClick={handleExportExcel}
          disabled={isExporting || (summary.totalProjects === 0)}
          title="Export to Excel"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Error State */}
      {fetchError && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mb-3" />
          <p className="text-muted-foreground text-sm max-w-md mb-4">{fetchError}</p>
          <Button variant="outline" size="sm" onClick={() => { setFetchError(null); setCurrentPage(1); }}>
            Try Again
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      ) : fetchError ? null : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPI
            label="Total EAC Cost"
            value={<SensitiveData>{formatCurrency(summary.totalEac)}</SensitiveData>}
            period={`vs ${formatCurrency(summary.totalBudget)} budget`}
            icon={<DollarSign className="w-4 h-4" />}
            accent="teal"
            variant="compact"
          />
          <KPI
            label="Total Budget Cost"
            value={<SensitiveData>{formatCurrency(summary.totalBudget)}</SensitiveData>}
            period={`across ${summary.totalProjects} projects`}
            icon={<Wallet className="w-4 h-4" />}
            accent="cerulean"
            variant="compact"
          />
          <KPI
            label="Forecast Variance"
            value={
              <SensitiveData>
                <span className={summary.forecastVariance > 0 ? 'text-error-600' : 'text-success-600'}>
                  {summary.forecastVariance > 0 ? '+' : ''}{formatCurrency(summary.forecastVariance)}
                </span>
              </SensitiveData>
            }
            period={
              summary.totalBudget > 0
                ? `${((summary.forecastVariance / summary.totalBudget) * 100).toFixed(1)}% ${summary.forecastVariance > 0 ? 'over budget' : 'under budget'}`
                : '—'
            }
            icon={summary.forecastVariance > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            accent={summary.forecastVariance > 0 ? 'destructive' : 'success'}
            variant="compact"
          />
          <KPI
            label="At-Risk Projects"
            value={summary.atRisk}
            period={`of ${summary.totalProjects} projects`}
            icon={<TriangleAlert className="w-4 h-4" />}
            accent="warning"
            variant="compact"
          />
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : fetchError ? null : enriched.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-muted-foreground mb-3" />
          {projects.length === 0 ? (
            <p className="text-muted-foreground text-sm max-w-xs">
              No active projects found. Projects with status 'In Progress' or 'Planning' will appear here.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">No projects match the current filters.</p>
          )}
        </div>
      ) : (
        <DataTableEnhanced
          data-tour="project-forecast-table"
          columns={columns}
          data={enriched}
          columnSorts={columnSorts}
          onColumnSortChange={handleColumnSortChange}
          onRowClick={handleRowClick}
          getRowId={row => String(row.project.projectId)}
          className="cursor-pointer"
          paginationContent={
            pagination && pagination.totalItems > 0 ? (
              <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            ) : undefined
          }
        />
      )}

      {/* Side Sheet */}
      {selectedRow && (
        <ForecastSideSheet
          row={selectedRow}
          globalMethod={globalMethod}
          onClose={() => setSelectedProjectId(null)}
        />
      )}
    </div>
  );
}
